import React, { useCallback, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import ColumnHeader, { SortType } from '../../../../../components/ColumnHeader';
import AllDivisionsPlayerCard from './AllDivisionsPlayerCard';
import Pagination from '../../../../../components/Pagination';
import Img from '../../../../../components/Img';
import { useStatsig } from '../../../../../components/hooks/useStatsig';
import { setPlayersTabState } from '../../../../../actions/event-profile-actions';
import { ratingTypes, sortParamTypes } from '../../../../../utils/constants';
import { freeDivision, paymentStatusTypeIds, playerListDataTypes, registrationGroupStatusTypeIds, registrationStatusTypeIds, singleDivision, sportTypeIds, utrTypeIds } from '../../../../../utils/eventConstants';
import { getPlayerPosition } from '../../../../../utils';

import styles from '../../../event.scss';
import { getAllDivisionsGridLayout } from '../../../../../utils/events';
import { playerShape } from '../../../../../propTypes';

export const getPlayerActionsUtil = (playerDivisions, freeDivisions, isStripe, allowOffPlatformPayments) => {
    let playerActions = [];
    const isFreeEvent = (divisionId) => {
        const matchedEvent = freeDivisions?.find(event => event.id === divisionId);
        return matchedEvent?.price === freeDivision;
    };

    const hasRegistered = playerDivisions?.some(div => div?.registrationStatusTypeId === registrationStatusTypeIds.REGISTERED);

    const hasWithdrawnPaid = playerDivisions?.some(div => div.registrationStatusTypeId === registrationStatusTypeIds.WITHDRAWN &&
        div.paymentStatusTypeId === paymentStatusTypeIds.PAID);

    if (hasRegistered || hasWithdrawnPaid) {
        const value = hasRegistered ? 'withdraw' : 'refund';
        const hasFreeDiv = playerDivisions.some(div => isFreeEvent(div.id));
        const showWithdrawLabel = hasRegistered && !isStripe && !allowOffPlatformPayments && !hasFreeDiv;
        playerActions = [
            { label: showWithdrawLabel ? 'Withdraw' : 'Manage', value },
        ];
    }
    return playerActions;
};

const AllDivisionsList = (props) => {
    const { players, showPlayers, isTennisSportEvent, isPickleBallSportEvent, isCurrentMemberAdmin, showRatingNotLoggedIn, utrTypeId, isOfficialHSEvent, auth, sendMessage, hideUtrLabel, sportTypeId, playersDictByMemberId, isStripe, allowOffPlatformPayments, showRegStatus, showPartner, toggleMessagePopUp, freeDivisions, showNeedPartners, handleTabClick, dataType, hasPublishedDraw, isEventStarted, onClickPlayerDivision } = props;

    const { event, playersTabState, draws } = useSelector(({ eventProfile }) => {
        const { event, playersTabState, draws } = eventProfile;
        return { event, playersTabState, draws };
    });
    const dispatch = useDispatch();
    const { checkGate, featureGates } = useStatsig();
    const showSaveBookmark = checkGate(featureGates.SAVED_PLAYER_LIST_ROLLOUT);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedPlayers, setSelectedPlayers] = useState([]);
    const pageSize = 100;
    const {
        sortParam,
        sortType,
        filterQuery = '',
        playerListDivisionFilters = [],
    } = playersTabState || {};

    const showDivisions = dataType !== playerListDataTypes.DRAWS;
    const showDraws = hasPublishedDraw && event.data?.divisionDrawAssociation && dataType !== playerListDataTypes.DIVISIONS;
    const eventSchedule = event.data?.eventSchedule || null;
    const timeZone = eventSchedule ? eventSchedule.timeZone : null;

    const isFilterActive = playersTabState.divisionId || playersTabState.selectedStatus?.value || playersTabState.selectedPosition?.value;

    const gridLayout = getAllDivisionsGridLayout({
        isCurrentMemberAdmin,
        showDivisions,
        showRegStatus,
        showPartner,
        showDraws,
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [filterQuery]);

    const filterPlayers = useCallback((players) => {
        return players
            .filter((player) => {
                // Remove players with no registered divisions but event entry fee division is present
                if (player?.divisions?.length === singleDivision) {
                    return !player.divisions[0].isEventEntryFeeDivision;
                }

                return true;
            })
            .filter((player) => {
                // Filter players by position filter
                if (playersTabState.selectedPosition?.value) {
                    return player.divisions?.some((div) => {
                        const playerPosition = getPlayerPosition(div);
                        return !div.isEventEntryFeeDivision && playersTabState.selectedPosition?.value?.toLowerCase().replace('_', ' ') === playerPosition?.bucket?.toLowerCase();
                    });
                }
                return true;
            })
            .filter((player) => {
                // Filter players by division filters
                if (playerListDivisionFilters?.length > 0) {
                    return player.divisions?.some((div) => playerListDivisionFilters.includes(div.id));
                }

                return true;
            });
    }, [singleDivision, playerListDivisionFilters, playersTabState.selectedPosition?.value]);

    const getFilteredPlayersBasedOnVisibility = (players, showPlayers, showNeedPartners, memberId) => {
        if (!showPlayers && !showNeedPartners) {
            return players?.filter((player) => {
                return player.memberId === memberId;
            });
        }
        if (!showPlayers && showNeedPartners) {
            return players?.filter((player) => {
                return player.memberId === memberId || player.divisions?.some(div => div.registrationGroupStatus === registrationGroupStatusTypeIds.NEED_PARTNER);
            });
        }
        return players;
    };

    const playerList = getFilteredPlayersBasedOnVisibility(players, showPlayers, showNeedPartners, auth.user?.id);

    const nonEntryFeeDivisionplayers = useMemo(() => {
        return filterPlayers(playerList);
    }, [filterPlayers, playerList]);

    const filteredPlayers = useMemo(() => {
        return nonEntryFeeDivisionplayers
            .filter(player => {
                const playerListFilter = filterQuery.replace(/[-[\]{}()*+?.,\\/^$|#\s]/g, '\\$&');
                const regex = new RegExp(playerListFilter, 'i');
                if (filterQuery) {
                    const playerFullName = `${player?.firstName} ${player?.lastName}`.replace(/\s+/g, ' ');
                    return !!regex.exec(playerFullName);
                }

                return true;
            });
    }, [nonEntryFeeDivisionplayers, filterQuery]);

    const paginatedPlayers = useMemo(() => {
        return filteredPlayers?.slice((currentPage - 1) * pageSize, currentPage * pageSize) || [];
    }, [filteredPlayers, currentPage, pageSize]);

    const lastPage = useMemo(() => {
        return filteredPlayers?.length ? Math.ceil(filteredPlayers?.length / pageSize) : 1;
    }, [filterPlayers, nonEntryFeeDivisionplayers, pageSize]);

    const filteredPlayersWithNonEntryDivisions = useMemo(() => {
        return filterPlayers(paginatedPlayers);
    }, [filterPlayers, paginatedPlayers]);

    const paginationClick = (page) => {
        setCurrentPage(page);
    };

    const allIsSelected = () => {
        return false;
    };

    const handleToggleAllClick = () => {
        if (selectedPlayers?.length === filteredPlayersWithNonEntryDivisions?.length || selectedPlayers?.length === nonEntryFeeDivisionplayers?.length) {
            setSelectedPlayers([]);
        } else {
            const udpatedSelection = filteredPlayersWithNonEntryDivisions.map(p => p.memberId);
            setSelectedPlayers(udpatedSelection);
        }
    };

    const onPlayerSelectClick = (memberId) => {
        let udpatedSelection = [...selectedPlayers];
        if (!selectedPlayers.includes(memberId)) {
            udpatedSelection.push(memberId);
        } else {
            udpatedSelection = udpatedSelection.filter(mid => mid !== memberId);
        }
        setSelectedPlayers(udpatedSelection);
    };

    const onSelectEveryPlayerClick = () => {
        const udpatedSelection = nonEntryFeeDivisionplayers.map(p => p.memberId);
        setSelectedPlayers(udpatedSelection);
    };

    const onClearSelectionClick = () => {
        setSelectedPlayers([]);
    };

    const onMessageClick = () => {
        toggleMessagePopUp(selectedPlayers);
    };

    const getPlayerActions = (playerDivisions) => getPlayerActionsUtil(playerDivisions, freeDivisions, isStripe, allowOffPlatformPayments);

    if (!nonEntryFeeDivisionplayers) {
        return <p data-testid="all-divisions-list.loading-msg">Players are loading...</p>;
    }
    if (!showPlayers && players.length > 0 && nonEntryFeeDivisionplayers?.length === 0) {
        return <p data-testid="all-divisions-list.hidden-msg">Registered players are hidden.</p>;
    }
    if (!nonEntryFeeDivisionplayers?.length) {
        return isFilterActive ? (
            <p data-testid="all-divisions-list.no-match-msg">
                There are no players matching your criteria.
            </p>
        ) : (
            <p data-testid="all-divisions-list.empty-msg">
                Waiting for players<br />
                Check back to view players registered for this event.
            </p>
        );
    }
    return (
        <>
            <div className="fw300 fs14" data-testid="all-divisions-list.player-count">Showing {filteredPlayers?.length} players</div>
            <div className={styles.playerListMargin}>
                <div className={styles.hScroll}>
                    <div className={styles.playerListWrapper} data-testid="event-profile.players.player-list-wrapper">
                        <div className="d-flex text-small fw600 player-list-header player-list-header-v3">
                            {isCurrentMemberAdmin && (
                                <div className={`d-flex align-items-center ${allIsSelected() ? 'selected' : ''}`}>
                                    <div className="checkbox-wrapper">
                                        <div className={`checkbox-check clickable ${selectedPlayers?.length === filteredPlayersWithNonEntryDivisions?.length || selectedPlayers?.length === nonEntryFeeDivisionplayers?.length ? 'checked' : ''}`} onClick={handleToggleAllClick} data-testid="all-divisions-list.toggle-all-checkbox"><Img src="/img/icons/checkmark-icon.svg" /></div>
                                    </div>
                                </div>
                            )}
                            {selectedPlayers.length ? (
                                <div className="flex-grow">
                                    <div className="row">
                                        <span className="text-dark mr16 ml8" data-testid="all-divisions-list.selected-count">
                                            {selectedPlayers.length} Selected
                                        </span> {selectedPlayers.length === nonEntryFeeDivisionplayers.length ? <span className="link clickable" onClick={onClearSelectionClick} data-testid="all-divisions-list.clear-selection">Clear Selection</span> : <span className="link clickable" onClick={onSelectEveryPlayerClick} data-testid="all-divisions-list.select-all">Select All Players</span>}
                                        <span className="ml16 mr16" data-testid="all-divisions-list.separator">|</span>
                                        <span className="link clickable" onClick={onMessageClick} data-testid="all-divisions-list.message-selected">
                                            <Img alt="message icon mr8" src={'/img/v4/message.svg'} /> Message Selected Players
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex-grow">
                                    <div className="row">
                                        <div className={`col-12`}>
                                            <div className="d-grid w-100" style={{ gridTemplateColumns: gridLayout.join(' ') }}>
                                                <ColumnHeader
                                                    isSortable
                                                    columnName={`Name`}
                                                    isSelected={sortParam === sortParamTypes.NAME}
                                                    sortType={sortType}
                                                    initialSort={SortType.ASC}
                                                    onClick={(srtType) => {
                                                        dispatch(setPlayersTabState({
                                                            ...playersTabState,
                                                            sortParam: sortParamTypes.NAME,
                                                            sortType: srtType,
                                                        }));
                                                    }}
                                                />
                                                {isTennisSportEvent && (
                                                    <>
                                                        <ColumnHeader
                                                            isSortable
                                                            columnName={ratingTypes.tennis}
                                                            isSelected={sortParam === sortParamTypes.SINGLESUTR}
                                                            sortType={sortType}
                                                            onClick={(srtType) => {
                                                                dispatch(setPlayersTabState({
                                                                    ...playersTabState,
                                                                    sortParam: sortParamTypes.SINGLESUTR,
                                                                    sortType: srtType,
                                                                }));
                                                            }}
                                                            imgUrl="/img/v3/icons/icon-singles-black.svg"
                                                        />
                                                        <ColumnHeader
                                                            isSortable
                                                            columnName={ratingTypes.tennis}
                                                            isSelected={sortParam === sortParamTypes.DOUBLESUTR}
                                                            sortType={sortType}
                                                            onClick={(srtType) => {
                                                                dispatch(setPlayersTabState({
                                                                    ...playersTabState,
                                                                    sortParam: sortParamTypes.DOUBLESUTR,
                                                                    sortType: srtType,
                                                                }));
                                                            }}
                                                            imgUrl="/img/v3/icons/icon-doubles-black.svg"
                                                        />
                                                    </>
                                                )}
                                                {isPickleBallSportEvent && (
                                                    <>
                                                        <ColumnHeader
                                                            isSortable
                                                            columnName={ratingTypes.pickleball}
                                                            isSelected={sortParam === sortParamTypes.SINGLESPKB}
                                                            sortType={sortType}
                                                            onClick={(srtType) => {
                                                                dispatch(setPlayersTabState({
                                                                    ...playersTabState,
                                                                    sortParam: sortParamTypes.SINGLESPKB,
                                                                    sortType: srtType,
                                                                }));
                                                            }}
                                                            imgUrl="/img/v3/icons/icon-singles-black.svg"
                                                        />
                                                        <ColumnHeader
                                                            isSortable
                                                            columnName={ratingTypes.pickleball}
                                                            isSelected={sortParam === sortParamTypes.DOUBLESPKB}
                                                            sortType={sortType}
                                                            onClick={(srtType) => {
                                                                dispatch(setPlayersTabState({
                                                                    ...playersTabState,
                                                                    sortParam: sortParamTypes.DOUBLESPKB,
                                                                    sortType: srtType,
                                                                }));
                                                            }}
                                                            imgUrl="/img/v3/icons/icon-doubles-black.svg"
                                                        />
                                                    </>
                                                )}
                                                {showDivisions && (
                                                    <ColumnHeader
                                                        columnName="Reg. divisions"
                                                    />
                                                )}
                                                {showDivisions && isCurrentMemberAdmin && (
                                                    <ColumnHeader
                                                        columnName="Reg. date"
                                                    />
                                                )}
                                                {showDivisions && showRegStatus && (
                                                    <ColumnHeader
                                                        columnName="Reg. status"
                                                    />
                                                )}
                                                {showDivisions && showPartner && (
                                                    <ColumnHeader
                                                        columnName="Reg. partner"
                                                    />
                                                )}
                                                {showDivisions && (
                                                    <ColumnHeader
                                                        columnName="Reg. position"
                                                    />
                                                )}
                                                {showDivisions && isCurrentMemberAdmin && (
                                                    <ColumnHeader columnName="Payment" />
                                                )}
                                                {showDraws && (
                                                    <ColumnHeader
                                                        columnName="Draw(s)"
                                                    />
                                                )}
                                                {showDraws && (
                                                    <ColumnHeader
                                                        columnName="Draw Partner"
                                                    />
                                                )}
                                                {showDraws && (
                                                    <ColumnHeader
                                                        columnName="Draw Status"
                                                    />
                                                )}
                                                {showDraws && (
                                                    <ColumnHeader
                                                        columnName="Player Status"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="bookmark">
                                <div className="options" />
                            </div>
                        </div>
                        {filteredPlayersWithNonEntryDivisions.map(player => {
                            const isOwnPlayerCard = player.memberId === auth.user?.id;
                            const playerActions = getPlayerActions(player?.divisions);
                            const showMenuOptions = isCurrentMemberAdmin && playerActions.length > 0;

                            return (
                                <div
                                    className={`${styles.teamCard} ${styles.eventProfileV3}`}
                                    key={`player-${player.memberId}${player.isWithdrawn ? '-w' : ''}`}
                                    data-testid={`event-profile.players.team-card-${player.memberId}`}
                                >
                                    <AllDivisionsPlayerCard
                                        addOptionsSpace={true}
                                        auth={auth}
                                        gridLayout={gridLayout}
                                        showSaveBookmark={showSaveBookmark}
                                        data-testid="event-profile.players"
                                        hideUtrLabel={hideUtrLabel}
                                        isCurrentMemberAdmin={isCurrentMemberAdmin}
                                        isSelected={selectedPlayers.includes(player.memberId)}
                                        menuOnSelected={(selected, e) => props.playerMenuOnSelected(e, player.playerId, selected)}
                                        menuOptions={showMenuOptions ? playerActions : null}
                                        onSelectClick={onPlayerSelectClick}
                                        player={player}
                                        playersDictByMemberId={playersDictByMemberId}
                                        sendMessage={sendMessage}
                                        showCheckbox={isCurrentMemberAdmin}
                                        showDecimals={isOwnPlayerCard || isCurrentMemberAdmin}
                                        showHighSchoolName={isOfficialHSEvent}
                                        showPartner={showPartner}
                                        showPickleBallRatings={isPickleBallSportEvent}
                                        showPlayerPaymentStatus={isCurrentMemberAdmin}
                                        showRatingNotLoggedIn={showRatingNotLoggedIn}
                                        showRegStatus={showRegStatus}
                                        showTennisRatings={isTennisSportEvent}
                                        sportTypeId={sportTypeId}
                                        utrTypeId={utrTypeId}
                                        showPlayers={showPlayers}
                                        showNeedPartners={showNeedPartners}
                                        showDivisions={showDivisions}
                                        showDraws={showDraws}
                                        drawsDict={draws?.drawsDict || {}}
                                        handleTabClick={handleTabClick}
                                        dataType={dataType}
                                        timeZone={timeZone}
                                        eventId={event?.data?.id}
                                        isEventStarted={isEventStarted}
                                        onClickPlayerDivision={onClickPlayerDivision}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            {lastPage > 1 && (
                <Pagination
                    lastPage={lastPage}
                    currentPage={currentPage}
                    onClick={paginationClick}
                />
            )}
        </>
    );
};

AllDivisionsList.propTypes = {
    allowOffPlatformPayments: PropTypes.bool,
    auth: PropTypes.shape({
        user: PropTypes.shape({
            id: PropTypes.number.isRequired,
        }).isRequired,
    }).isRequired,
    hideUtrLabel: PropTypes.bool,
    isCurrentMemberAdmin: PropTypes.bool,
    isOfficialHSEvent: PropTypes.bool,
    isPickleBallSportEvent: PropTypes.bool,
    isStripe: PropTypes.bool,
    isTennisSportEvent: PropTypes.bool,
    players: PropTypes.arrayOf(PropTypes.shape({
        memberId: PropTypes.number,
        playerId: PropTypes.number,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        divisions: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.number,
            isEventEntryFeeDivision: PropTypes.bool,
            registrationGroupStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            registrationStatusTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            paymentStatusTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            partners: PropTypes.arrayOf(PropTypes.number),
        })),
    })).isRequired,
    playersDictByMemberId: PropTypes.objectOf(playerShape).isRequired,
    sendMessage: PropTypes.func,
    showPlayers: PropTypes.bool,
    showRatingNotLoggedIn: PropTypes.bool,
    showRegStatus: PropTypes.bool,
    sportTypeId: PropTypes.number,
    toggleMessagePopUp: PropTypes.func,
    handleTabClick: PropTypes.func,
    utrTypeId: PropTypes.number,
    freeDivisions: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        price: PropTypes.string,
    })),
    showNeedPartners: PropTypes.bool,
    dataType: PropTypes.string,
    hasPublishedDraw: PropTypes.bool,
    isEventStarted: PropTypes.bool,
    onClickPlayerDivision: PropTypes.func.isRequired,
};
AllDivisionsList.defaultProps = {
    allowOffPlatformPayments: false,
    hideUtrLabel: false,
    isCurrentMemberAdmin: false,
    isOfficialHSEvent: false,
    isPickleBallSportEvent: false,
    isStripe: false,
    isTennisSportEvent: true,
    sendMessage: () => { },
    showPlayers: false,
    showRatingNotLoggedIn: false,
    showRegStatus: false,
    sportTypeId: sportTypeIds.TENNIS,
    toggleMessagePopUp: () => { },
    handleTabClick: () => { },
    utrTypeId: utrTypeIds.MYUTR,
    freeDivisions: [],
    showNeedPartners: false,
    dataType: '',
    hasPublishedDraw: false,
    isEventStarted: false,
};

export default AllDivisionsList;
